'use client';

import { Menu, Transition } from '@headlessui/react';
import { cloneElement, FC, Fragment, ReactElement } from 'react';
import UseAnimations from 'react-useanimations/';
import { Animation } from 'react-useanimations/utils';
import { cn } from 'utils/cn';
interface IDropdown {
  children?: ReactElement<{ open?: boolean }>;
  header?: ReactElement<{ open?: boolean }>;
  items?: {
    name?: string;
    href?: string;
    locale?: string;
    icon?: Animation;
    content?: ReactElement;
    onClick?: () => void;
  }[];
  className?: string;
  hidden?: boolean;
  noHighlight?: boolean;
  itemsRow?: boolean;
  arrow?: boolean;
  variant?: 'hamburger' | 'default';
  position?: 'up' | 'down';
}

const Dropdown: FC<IDropdown> = ({
  children,
  header,
  items,
  noHighlight,
  itemsRow,
  arrow,
  className,
  hidden,
  variant = 'default'
}) => {
  return (
    <Menu
      as="div"
      className={cn('relative flex h-full items-center justify-center text-left', {
        'opacity-0': hidden
      })}
    >
      <Menu.Button
        className={cn(
          'outline-none',
          'group flex size-full items-center rounded-md p-2 text-sm capitalize',
          !noHighlight && 'hover:bg-opacity-25',
          !noHighlight && variant === 'default' && 'hover:bg-tolq-blue-200'
        )}
      >
        {(props) => <>{cloneElement(children ?? <></>, props)}</>}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-600"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={cn(
            'focus:outline-none',
            'absolute -top-2 right-0 origin-right transform divide-y divide-gray-100',
            'lg:-right-[125px]',
            variant === 'hamburger'
              ? 'w-[100dvw] translate-x-[24px] translate-y-[58px] bg-[hsla(0,0%,100%,.80)] shadow-box-hamburguer backdrop-blur-xl sm:translate-x-[64px] sm:translate-y-[64px]'
              : 'w-56 origin-right -translate-y-[calc(100%+10px)] rounded-md bg-white shadow-lg  ring-1 ring-black ring-opacity-5',
            className
          )}
        >
          <div className="flex flex-col">
            {arrow && (
              <div
                className={cn(
                  'absolute rotate-45 bg-white',
                  '!h-5 !w-5',
                  'right-4',
                  'bottom-[-8px]'
                )}
              />
            )}
            {header}
            {variant === 'hamburger' && (
              <span className="mx-8 border-b-1 border-tolq-gray-100 opacity-20" />
            )}
            <div
              className={cn(
                'flex ',
                variant === 'hamburger' ? 'p-4' : 'p-1',
                itemsRow ? 'flex-row flex-wrap gap-2 !p-2 pt-0' : 'flex-col'
              )}
            >
              {items?.map(({ href, name, icon, content, locale, onClick }, index) => (
                <Menu.Item key={'menu-item' + index} as={Fragment}>
                  {({ active }) => {
                    const Content = () =>
                      content ? (
                        <>{content}</>
                      ) : (
                        <>
                          {name}
                          {icon && (
                            <UseAnimations animation={icon} className="pointer-events-none" />
                          )}
                        </>
                      );

                    const classname = cn(
                      'ui-active:bg-blue-500 ui-active:text-white ui-not-active:bg-white ui-not-active:text-black',
                      'group items-center rounded-md text-sm capitalize',
                      'flex w-full justify-between',
                      noHighlight ? '' : 'p-2 hover:bg-tolq-gray-200 hover:text-tolq-blue-100'
                    );

                    return (
                      <span>
                        {href ? (
                          <a
                            href={href}
                            className={cn(
                              classname,
                              active ? 'bg-gray-100' : '',
                              variant === 'hamburger' &&
                                'w-[93dvw] !px-4 !py-4 text-xl font-semibold leading-normal text-black'
                            )}
                            {...(locale && { locale })}
                          >
                            <Content />
                          </a>
                        ) : (
                          <button
                            onClick={onClick}
                            className={cn(classname, active ? 'bg-gray-100' : '')}
                          >
                            <Content />
                          </button>
                        )}
                      </span>
                    );
                  }}
                </Menu.Item>
              ))}
            </div>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default Dropdown;
